import { InitType } from "../blueprint";
import { StateServices } from "../../instance-service/i-state-services";
import { Vector3 } from "three";
import { RenderServices } from "../../instance-service/i-render-services";
import { Surface } from "../../../surface/primitives/surface";
import { Entity } from "src/app/state/entities/entity.model";
import {IMetadata} from "../../instance-service/i-metadata";
import {Subscription} from "rxjs";
import {BlueprintContext} from "../i-blueprint-context";
import {BlueprintSurface} from "../blueprint-movable";
import {
  selectEntityById,
  selectTextComponent, selectUsersTrackComponent
} from "../../../../state/entities/entity.selector";
import {PixiObject} from "../../../rendering/pixi/primitives/pixi-object";
import {TouchFollower} from "../../../rendering/pixi/drawables/touch-follower.pixi";
import {UsersTrackProperties} from "../../../../state/entities/component-properties-dto/users-track.properties.dto";
import {TextDrawable} from "../../../rendering/pixi/drawables/text.pixi";
import {TextStyle} from "pixi.js";
import {User} from "../../../../auth/primitives/user";
import {ProfileImageComponent} from "../../../../interface-components/profile-image/profile-image.component";
import {ProfileImageDrawable} from "../../../rendering/pixi/drawables/profile-image.pixi";
import {PixiZDepthLayers} from "../../../surface/canvas/pixi-z-depth-layers.enum";
import {animate, JSAnimation} from "animejs";
import {selectUserById} from "../../../../state/users/users.selector";


export function emojiTextStyle(): TextStyle {
  const fontSize = 200;
  const textStyle = new TextStyle({
    fontFamily: ["inter"],
    fontSize: fontSize,
    fontWeight: "normal",
    fill: "#aaaaaa",
    wordWrap: true,
    wordWrapWidth: 315,
    breakWords: true,
    align: "left",
  });
  return textStyle;
}
export function userTrackTextStyle(): TextStyle {
  const fontSize = 18;
  const textStyle = new TextStyle({
    fontFamily: ["inter"],
    fontSize: fontSize,
    fontWeight: "normal",
    fill: "#aaaaaa",
    wordWrap: true,
    wordWrapWidth: 315,
    breakWords: true,
    align: "left",
  });
  return textStyle;
}
export class MapReactionBlueprint extends BlueprintSurface {
  override id: string;
  override type: string = "MapReaction"

  isOwners: boolean

  userTrackAlphaAnim: JSAnimation

  override async init(config: {
    initType: InitType,
    metadata: IMetadata
  } & BlueprintContext) {
    this.log("Starting")
    this.isOwners = config.metadata.isOwners
    await super.init(config)
    await this.setupSurface(250, 250)
    this.setupReactiveBindings()
    this.log("Handlers configured")
  }

  override destroy(): void {
    this.surface.destroy()
  }

  override makeSurface(config: { entity: Entity; initType: InitType; stateServices: StateServices; renderServices: RenderServices; }): Promise<Surface> {
    throw new Error("Method not implemented.");
  }

  private setupReactiveBindings() {
    this.subscriptions.push(
        this.blueprintContext.stateServices.store
            .select(selectEntityById({entityId: this.entity.id})).subscribe(async (entity) => {
              if (!entity) {
                return
              }
            this.entity = entity;
          await this.createOrUpdateProfileImage()
        })
    )
    this.subscriptions.push(
        this.blueprintContext.stateServices.store
            .select(selectUsersTrackComponent(this.entity.id)).subscribe(async (usersTrack: UsersTrackProperties) => {
          if (!usersTrack) {
            return
          }
          await this.createOrUpdateUsersTrack(usersTrack)
        })
    )
    this.subscriptions.push(
        this.blueprintContext.stateServices.store
            .select(selectTextComponent(this.entity.id)).subscribe(async (text: string) => {
          if (!text) {
            return
          }
          await this.createOrUpdateMainEmoji(text)
        })
    )
    this.subscriptions.push(
        this.blueprintContext.stateServices.store
            .select(selectUserById(this.entity.ownerId)).subscribe(async (user: User) => {
          if (!user) {
            return
          }
          await this.createOrUpdateUserName(user)
        })
    )
  }

  async createOrUpdateMainEmoji(text: string) {
    this.setComponent('emoji', () => {
      return new TextDrawable({
        margin: { left: 5 },
        text: text,
        style: emojiTextStyle(),
      })
    })
    await this.layout()
  }

  async createOrUpdateUsersTrack(usersTrackProperties: UsersTrackProperties) {
    this.setComponent('userTrack', () => {
      return new TextDrawable({
          margin: { left: 5 },
          text: usersTrackProperties.count,
          style: userTrackTextStyle(),
      })
    })
    await this.layout()
  }

  async createOrUpdateProfileImage() {
    this.setComponent('profileImage', () => {
      return new ProfileImageDrawable({
        isMe: false,
        blueprintContext: this.blueprintContext,
        width: 30
      })
    })
    await this.layout()
  }

  async createOrUpdateUserName(user: User) {
    this.setComponent('profileText', () => {
      return new TextDrawable({
        margin: { left: 5 },
        text: user.display_name,
        style: userTrackTextStyle(),
      })
    })
    await this.layout()
  }


  async setupSurface(width: number, height: number) {
    this.log(`Width: ${width}, height: ${height}`)
    this.surface = await this.renderServices.surfaceService.create({
      left: this.entity.coord.x,
      top: this.entity.coord.y,
      width: width,
      height: height + 50 // Allow for social bar at bottom
    })
    this.log("surface created")
    // this.surface.getTouchPrimitive().onHover((async (pt) => {
    //   return false
    // }).bind(this))
    this.setupSurfaceMovementHandlers(this.surface, this.isOwners)
    this.surface.mesh.position.z = 70
    this.surface.stage.zIndex = PixiZDepthLayers.MapReactions
  }


  private async layout() {
    if (["profileImage", "profileText", "userTrack", "emoji"].every(component => this.components.has(component))) {
      const emoji = this.components.get('emoji')
      const profileImage = this.components.get('profileImage')
      const profileText = this.components.get('profileText')
      const userTrack = this.components.get('userTrack')
      const combinedWidth = profileImage.width + profileText.width + userTrack.width + 17
      const newX = (emoji.width / 2) - (combinedWidth / 2)
      profileImage.touchPrimitive.showPointerCursor = true
      profileText.touchPrimitive.showPointerCursor = true
      userTrack.touchPrimitive.showPointerCursor = true
      profileImage.x = newX
      profileImage.y = emoji.height + 10
      profileText.x = newX + profileImage.width + 4
      profileText.y = emoji.height + 13
      userTrack.x = newX +profileImage.width + profileText.width + 14
      userTrack.y = emoji.height + 13
      this.surface.getTouchPrimitive().onHover(this.surfaceHover.bind(this))
      this.surface.getTouchPrimitive().onHoverNear(this.surfaceHoverNear.bind(this))
      emoji.updateTouchBounds()
    }
  }

  surfaceHoverNear() {
    const markForRender = this.components.get('profileImage').sprite.alpha === 1

    this.userTrackAlphaAnim = animate([
      this.components.get('profileImage').sprite,
      this.components.get('profileText').sprite,
      this.components.get('userTrack').sprite
    ], {
        alpha: 0,
        duration: 200,
    });
    if (markForRender) this.surface.markForRender()
    return true
  }

  surfaceHover(pt) {
    const markForRender = this.components.get('profileImage').sprite.alpha === 0
    this.userTrackAlphaAnim = animate([
      this.components.get('profileImage').sprite,
      this.components.get('profileText').sprite,
      this.components.get('userTrack').sprite
    ], {
      alpha: 1,
      duration: 200,
    });
    if (markForRender) this.surface.markForRender()
    return false
  }


  override updatePosition(config: { newPos: Vector3; }) {
    if (!this.isMoving) {
      this.surface.mesh.position.x = config.newPos.x;
      this.surface.mesh.position.y = config.newPos.y;
    }
  }

}
